import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRightIcon, CoinsIcon } from 'lucide-react';

import {
  DISPLAY_FIGURE,
  PANEL,
  QUIET_FIGURE,
} from '@/features/workspace-overview/stateStyles';
import { DeletedLabel } from '@/shared/components/DeletedLabel';
import { SECTION_HEADING } from '@/shared/components/sectionHeading';
import {
  IncompletenessNotice,
  PeriodStepper,
  PeriodTypeSelector,
  THRESHOLD_CHIP,
  THRESHOLD_CHIP_BOX,
  THRESHOLD_LABEL,
  exactMoney,
  formatMoney,
  formatTokens,
  isForbidden,
} from '@/features/workspace-usage';
import { buttonVariants } from '@/components/ui/button';
import { EmptyState } from '@/shared/components/EmptyState';
import { RetryButton } from '@/shared/components/RetryButton';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { ADMIN_PAGE_SECTION, ADMIN_TILE_GRID } from '../adminLayout';
import { CONSOLE_ANSWER_COPY as ANSWER } from '../consoleCopy';
import { ConsoleAnswer, ConsoleHeader } from './ConsoleHeader';
import { AdminOverviewSkeleton } from './AdminSkeletons';
import { InstallationLoad } from './InstallationLoad';
import { OwnerlessWorkspaces } from './OwnerlessWorkspaces';
import type { OrganizationUsageSummary } from '@/lib/api/generated/model';

import { useOrganizationUsage } from '../hooks/useOrganizationUsage';

/**
 * What the organization is spending, and how close its workspaces are to their limits
 * (spec 073 US1).
 *
 * Four figures now, not three. The prototype this came from showed four counters —
 * Workspaces, Claude connections, Default limit, Role mappings — of which two counted
 * features that did not exist yet and would have read zero forever, so both were
 * dropped: a tile that cannot vary teaches people to skip the row it sits in. The LLM
 * connections count is one of those two, brought back (spec 085, FR-12) now that the
 * `ai-providers` feature is real and the count means something. The other — role
 * mappings — still counts nothing and stays out.
 */
export interface AdminOverviewPageProps {
  /** The calendar month being read, or `undefined` for the current one. */
  period?: string;
  onPeriodChange: (periodKey: string | undefined) => void;
}

export function AdminOverviewPage({
  period,
  onPeriodChange,
}: AdminOverviewPageProps) {
  const usage = useOrganizationUsage(period);
  const showPlaceholder = usePendingDisplay(usage.isPending);

  // The frame stays put while the figures load (spec 079 FR-005); an error now appears
  // beneath the same header rather than in place of it (FR-008).
  if (usage.isPending) {
    return (
      <section className={ADMIN_PAGE_SECTION}>
        <OverviewHeader onPeriodChange={onPeriodChange} />
        {showPlaceholder ? <AdminOverviewSkeleton /> : null}
      </section>
    );
  }

  if (usage.isError) {
    return (
      <section className={ADMIN_PAGE_SECTION}>
        <OverviewHeader onPeriodChange={onPeriodChange} />
        <EmptyState
          icon={CoinsIcon}
          description={
            isForbidden(usage.error)
              ? 'Only an organization administrator can see what the organization is spending.'
              : 'These figures could not be read just now.'
          }
          action={
            // FR-027, FR-028 — offered on a transient failure, withheld on a refusal.
            isForbidden(usage.error) ? undefined : (
              <RetryButton
                onRetry={() => void usage.refetch()}
                isFetching={usage.isFetching}
              />
            )
          }
        />
      </section>
    );
  }

  const summary = usage.data;

  return (
    <section className={ADMIN_PAGE_SECTION}>
      <OverviewHeader summary={summary} onPeriodChange={onPeriodChange} />

      {summary.workspaces.length === 0 ? (
        // The new-deployment case, said rather than drawn. Zeroed tiles beside an empty
        // table read as a page that failed to load, which is the one impression a fresh
        // installation should not give.
        <>
          <EmptyState
            icon={CoinsIcon}
            description="No workspaces have recorded any usage yet. Once work runs, what it costs will appear here."
          />
          <OwnerlessWorkspaces />
        </>
      ) : (
        <>
          <div className={ADMIN_TILE_GRID}>
            {/*
              Tokens beside money, not instead of it (contracts.py). Money is what a
              limit is denominated in and what the console is held to — but it is
              derived from a model's recorded price, so a model nobody has priced
              contributes tokens and no cost. The token figure is the one that stays
              true while pricing is incomplete, which is exactly when the money figure
              is least trustworthy and an administrator most needs a second reading.
            */}
            <Tile
              label="Tokens"
              value={formatTokens(summary.totals.total_tokens)}
              detail={
                <span className="tabular-nums">
                  {formatTokens(summary.totals.input_tokens)} in ·{' '}
                  {formatTokens(summary.totals.output_tokens)} out
                </span>
              }
            />
            <CountTile
              label="At their limit"
              count={summary.workspaces_at_limit}
              threshold="exhausted"
              detail="Stopped until the limit is raised"
            />
            <CountTile
              label="Approaching"
              count={summary.workspaces_approaching_limit}
              threshold="approaching"
              detail="Still running, close to stopping"
            />
            {/*
              Built on `Tile` directly, not `CountTile` (spec 085 FR-004, FR-005):
              there is no good/bad threshold for a plain count, so no chip, and zero
              is a real state worth an administrator's plain attention rather than the
              dimmed "nothing wrong" treatment `CountTile` gives a zero.
            */}
            <Link
              to="/admin/ai-providers"
              className="focus-visible:ring-ring rounded-lg focus-visible:ring-3 focus-visible:outline-none"
            >
              <Tile
                label="LLM connections"
                value={String(summary.ai_providers_count)}
                detail="Configured for this organization"
              />
            </Link>
          </div>

          {/*
            Workspaces nobody can manage (spec 195 US5): right under the tiles, the
            one thing here that asks for an action. Under them, not above: it is
            its own read and lands after the usage, and above the tiles its
            arrival would push them down (spec 079).
          */}
          <OwnerlessWorkspaces />

          {/* The installation's load beside what it spends (spec 116 US3). */}
          <InstallationLoad />

          {/*
            The same notice the workspace page shows, over every workspace at once
            (FR-009, FR-027). Sharing the component is what stops the two surfaces
            explaining an incomplete figure differently — and this is the surface where
            it matters most, since the organization total is the figure most likely to
            be quoted to somebody.
          */}
          <IncompletenessNotice
            unmeasuredRuns={summary.spend.unmeasured_run_count}
            unpricedRuns={summary.spend.unpriced_run_count}
            action={
              // The fix-it path for the unpriced-runs case, inside the same box rather
              // than a second block beneath it: it used to be a whole panel here
              // (spec 077 US1), naming each unpriced model with its own "Add rate"
              // button. That panel is gone (spec 092) — the LLM Connections & Models
              // page now shows the same state inline on the model it belongs to, so
              // this is one prominent link there rather than a second place that
              // manages pricing.
              <Link
                to="/admin/ai-providers"
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                Fix unpriced models
                <ArrowRightIcon aria-hidden="true" />
              </Link>
            }
          />

          <ClosestToTheirLimits summary={summary} />

          <DefaultLimit summary={summary} />
        </>
      )}
    </section>
  );
}

/** How many workspaces the overview names before it stops being a summary. */
const NAMED_LIMIT = 5;

/**
 * The workspaces nearest their limits, by name (spec 074 FR-012, and `073` SC-002).
 *
 * The console reported *how many* workspaces were at risk and not *which*, so an
 * administrator who saw "2" had to open another page to learn anything they could act
 * on — and `073`'s own success criterion asks them to be able to name the closest one
 * from the first screen. The data was already in the response the page fetches; only the
 * rendering was missing.
 *
 * **Not re-ordered and not filtered** (FR-033). The service orders these by proximity to
 * the limit, and that ordering *is* the requirement — a client-side sort would silently
 * disagree with it the day either changes, and a filter could drop the workspace the
 * administrator opened the page for. So this takes the first few, as sent.
 *
 * The threshold word travels with the row, so the state is never carried by position
 * alone: being first in a list of calm workspaces means something quite different from
 * being first in a list where one has stopped.
 */
function ClosestToTheirLimits({
  summary,
}: {
  summary: OrganizationUsageSummary;
}) {
  const currency = currencyOf(summary);
  const named = summary.workspaces.slice(0, NAMED_LIMIT);

  return (
    <section className="flex flex-col gap-3" aria-labelledby="admin-closest">
      <h2 id="admin-closest" className={SECTION_HEADING}>
        Closest to their limits
      </h2>
      <ul className={PANEL}>
        {named.map((row) => (
          <li
            key={row.workspace_id}
            className="border-border flex items-center justify-between gap-4 border-t px-4 py-2.5 first:border-t-0"
          >
            <div className="flex min-w-0 flex-col">
              {/*
                FR-013: to that workspace's usage page and nowhere else. The console may
                not offer a route into a workspace's files, tasks or secrets (`073`
                FR-006), so this is deliberately not the workspace root.
              */}
              {row.deleted ? (
                /*
                  Spec 146 FR-002: a deleted workspace keeps its line and its spend,
                  under its last name, and links nowhere — it has no usage page left.
                */
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-sm font-medium">
                    {row.workspace_name}
                  </span>
                  <DeletedLabel />
                </span>
              ) : (
                <Link
                  to="/workspaces/$workspaceId/ai-usage/spend"
                  params={{ workspaceId: row.workspace_id }}
                  className="focus-visible:ring-ring truncate rounded-sm text-sm font-medium hover:underline focus-visible:ring-3 focus-visible:outline-none"
                >
                  {row.workspace_name}
                </Link>
              )}
              <span className="text-muted-foreground text-xs tabular-nums">
                {formatMoney(row.spend, row.currency ?? currency)}
                {row.deleted
                  ? ' spent'
                  : row.limit === null
                    ? ' spent · no limit applies'
                    : ` of ${formatMoney(row.limit, row.currency ?? currency)}`}
                {' · '}
                {formatTokens(row.total_tokens)} tokens
              </span>
            </div>
            {/*
              Only where there is something to say (FR-025). A chip on every row —
              including "Within limit" on the calm majority — is the same noise the
              zero chip on the tiles was: it makes the row that needs attention no
              more visible than the ones that do not.
            */}
            {row.threshold === null || row.threshold === 'under' ? null : (
              <span
                className={`${THRESHOLD_CHIP_BOX} ${THRESHOLD_CHIP[row.threshold]}`}
              >
                {THRESHOLD_LABEL[row.threshold]}
              </span>
            )}
          </li>
        ))}
      </ul>
      {summary.workspaces.length > NAMED_LIMIT ? (
        <p className="text-muted-foreground text-xs">
          {summary.workspaces.length - NAMED_LIMIT} more on the{' '}
          <Link
            to="/admin/limits"
            className="text-primary focus-visible:ring-ring rounded-sm hover:underline focus-visible:ring-3 focus-visible:outline-none"
          >
            limits page
          </Link>
          .
        </p>
      ) : null}
    </section>
  );
}

/**
 * One figure on the overview, with its label above and its detail beneath.
 *
 * The chip and the detail are *content*, passed in — which is what lets the money tile
 * and the count tiles share this markup instead of each keeping their own copy of it.
 * They had two copies, and the copies had already drifted: only one of them knew about
 * the quiet zero.
 */
function Tile({
  label,
  value,
  detail,
  chip,
  quiet = false,
}: {
  label: string;
  value: string;
  detail: ReactNode;
  /** The state this tile is in, where there is a state worth naming. */
  chip?: ReactNode;
  /** Nothing to report: the figure recedes rather than disappearing. */
  quiet?: boolean;
}) {
  return (
    <div className="border-border bg-card flex flex-col gap-1 rounded-lg border px-4 py-3.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-xs">{label}</p>
        {chip}
      </div>
      {/*
        `DISPLAY_FIGURE` rather than the class string this page had copied: the size and
        the "no `tabular-nums`" rule now live in one place, so the console and the
        workspace overview cannot disagree about them again (spec 074 FR-003).
      */}
      <p className={`${DISPLAY_FIGURE} ${quiet ? QUIET_FIGURE : ''}`}>
        {value}
      </p>
      <p className="text-muted-foreground/70 text-xs">{detail}</p>
    </div>
  );
}

/**
 * A count of workspaces in one threshold state.
 *
 * Carries the state as a word as well as a colour (`073` FR-025) — the same vocabulary
 * the workspace page uses for a single workspace, so "approaching" means one thing in
 * this product rather than two.
 *
 * **The chip appears only above zero** (spec 074 FR-005). A red "Limit reached" beside a
 * count of nothing was the console's most alarming element and it was reporting that
 * nothing was wrong; an organization with nothing wrong must show no alarm colour at all
 * (SC-004). The zero itself stays, quietly (FR-004) — suppressing it would make a calm
 * organization indistinguishable from one whose figures failed to load.
 */
function CountTile({
  label,
  count,
  threshold,
  detail,
}: {
  label: string;
  count: number;
  threshold: 'approaching' | 'exhausted';
  detail: string;
}) {
  const live = count > 0;

  return (
    <Tile
      label={label}
      value={String(count)}
      quiet={!live}
      chip={
        live ? (
          <span
            className={`${THRESHOLD_CHIP_BOX} ${THRESHOLD_CHIP[threshold]}`}
          >
            {THRESHOLD_LABEL[threshold]}
          </span>
        ) : null
      }
      detail={live ? detail : 'None'}
    />
  );
}

/** The default, stated here and changed on the limits page. */
function DefaultLimit({ summary }: { summary: OrganizationUsageSummary }) {
  return (
    <p className="text-muted-foreground text-sm">
      {summary.default_limit === null ? (
        <>
          No organization default is set, so a workspace without an override may
          spend without limit.
        </>
      ) : (
        <>
          The organization default is{' '}
          <span className="text-foreground font-medium">
            {formatMoney(summary.default_limit, currencyOf(summary))}
          </span>{' '}
          per period, for every workspace without an override.
        </>
      )}{' '}
      <Link
        to="/admin/limits"
        className="text-primary focus-visible:ring-ring rounded-sm hover:underline focus-visible:ring-3 focus-visible:outline-none"
      >
        Change limits
      </Link>
    </p>
  );
}

function currencyOf(summary: OrganizationUsageSummary): string {
  return summary.spend.currency ?? 'USD';
}

/**
 * The page's own furniture, extracted because three branches now render it — pending,
 * failed and loaded (spec 079 FR-005, FR-008). Inline copies in each would be three
 * chances for the header to differ depending on how the request went.
 */
function OverviewHeader({
  summary,
  onPeriodChange,
}: {
  /** Absent while the figures are loading or after they failed — the stepper needs a
   *  period to step from, and the header is drawn in all three states. */
  summary?: OrganizationUsageSummary;
  onPeriodChange: (periodKey: string | undefined) => void;
}) {
  return (
    <ConsoleHeader
      title="Overview"
      description="What every workspace has consumed in the selected period, and how many of them are close to the limit they are held to."
      answer={
        summary ? (
          <ConsoleAnswer
            figure={formatMoney(summary.spend.amount, currencyOf(summary))}
            words={ANSWER.overviewWords}
            detail={
              // Digit by digit, against an invoice: the one figure here that
              // is meant to be read that way (the tile it came from, spec 168).
              <span className="tabular-nums">
                {ANSWER.exact(
                  exactMoney(summary.spend.amount, currencyOf(summary)),
                )}
              </span>
            }
          />
        ) : undefined
      }
      actions={
        summary ? (
          // The workspace page's own controls, not a second pair. The console reads the
          // same four period types off the same `_resolve_period` (spec 085), so giving
          // it its own stepper or its own granularity list would be two chances to
          // offer, bound or word a period differently from the page an administrator
          // reaches by clicking a workspace in this very table.
          <div className="flex flex-col items-end gap-1.5">
            <PeriodStepper
              period={summary.period}
              earliestPeriodKey={summary.earliest_period_key}
              onChange={onPeriodChange}
            />
            <PeriodTypeSelector
              period={summary.period}
              onChange={onPeriodChange}
            />
          </div>
        ) : undefined
      }
    />
  );
}
